/* eslint-disable max-lines */
import { HealthSurveillanceSectionsConfig, MedicalTypeIds } from "@teamprevent/common/healthSurveillance/models";

import { confidentialityDeclarationQuestions } from "./questions/confidentialityDeclarationQuestions";
import { audiometryQuestions } from "./questions/audiometryQuestions";
import { healthQuestions } from "./questions/healthQuestions";
import { drugAlcoholTestQuestions } from "./questions/drugAlcoholTestQuestions";
import { urinalysisQuestions } from "./questions/urinalysisQuestions";
import { msdQuestions } from "./questions/msdQuestions";
import { skinQuestions } from "./questions/skinQuestions";
import { havsQuestions } from "./questions/havsQuestions";
import { examinationQuestions } from "./questions/examinationQuestions";
import { spirometryQuestions } from "./questions/spirometryQuestions";
import { visionQuestions } from "./questions/visionQuestions";
import { biologicalMonitoringQuestions } from "./questions/biologicalMonitoringQuestions";
import { manualHandlingMSKQuestionnaireQuestions } from "./questions/manualHandlingMSKQuestionnaireQuestions";
import { generalAndConsentQuestions } from "./questions/generalAndConsentQuestions";
import { chesterStepQuestions } from "./questions/chesterStepQuestions";
import { gripStrengthQuestions } from "./questions/gripStrengthQuestions";
import { bideltoidMeasurementQuestions } from "./questions/bideltoidMeasurementQuestions";
import { otherQuestions } from "./questions/otherQuestions";

type ConsultationSectionKey = "ConfidentialityDeclaration"
    | "HealthQuestions"
    | "DrugAlcoholTest"
    | "Urinalysis"
    | "MsdAssessment"
    | "SkinAssessment"
    | "Havs"
    | "Examination"
    | "Audiometry"
    | "Spirometry"
    | "Vision"
    | "BiologicalMonitoring"
    | "ManualHandlingMSKQuestionnaire"
    | "GeneralClinicNotes"
    | "BideltoidMeasurement"
    | "GripStrength"
    | "ChesterStep"
    | "Other";

export const sections: HealthSurveillanceSectionsConfig<ConsultationSectionKey> = {
    ConfidentialityDeclaration: {
        id: "ConfidentialityDeclaration",
        title: "Confidentiality Declaration",
        medicalTypes: [
            MedicalTypeIds.SkinFaceToFace,
            MedicalTypeIds.Audiometry,
            MedicalTypeIds.RespiratorySensitiser,
            MedicalTypeIds.RespiratorySensitiserFaceToFaceLevel1,
            MedicalTypeIds.RespiratorySensitiserQuestionnaireLevel1,
            MedicalTypeIds.HAVSFaceToFace,
            MedicalTypeIds.IonisingRadiationFaceToFace,
            MedicalTypeIds.BiologicalMonitoring,
            MedicalTypeIds.OtherSurveillance,
            MedicalTypeIds.SafetyCriticalWorker,
            MedicalTypeIds.GroupOneDriving,
            MedicalTypeIds.GroupTwoDriving,
            MedicalTypeIds.WorkAtHeight,
            MedicalTypeIds.WorkingConfinedSpaces,
            MedicalTypeIds.WearingRPE,
            MedicalTypeIds.WearingBreathingApparatus,
            MedicalTypeIds.WorkingThermalEnvironment,
            MedicalTypeIds.DSE,
            MedicalTypeIds.Offshore,
            MedicalTypeIds.Airside,
            MedicalTypeIds.OtherFitnessToWork,
            MedicalTypeIds.ManualHandlingMSKFaceToFaceAppointment,
            MedicalTypeIds.AmzPitPupMedical,
            MedicalTypeIds.LAAWorker,
            MedicalTypeIds.SkinFaceToFaceLevel1,
            MedicalTypeIds.SkinAssessmentLevel2
        ],
        questionConfigs: confidentialityDeclarationQuestions
    },
    HealthQuestions: {
        id: "HealthQuestions",
        title: "Health Questions",
        medicalTypes: [
            MedicalTypeIds.SkinFaceToFace,
            MedicalTypeIds.Audiometry,
            MedicalTypeIds.RespiratorySensitiser,
            MedicalTypeIds.RespiratorySensitiserFaceToFaceLevel1,
            MedicalTypeIds.RespiratorySensitiserQuestionnaireLevel1,
            MedicalTypeIds.HAVSFaceToFace,
            MedicalTypeIds.IonisingRadiationFaceToFace,
            MedicalTypeIds.BiologicalMonitoring,
            MedicalTypeIds.OtherSurveillance,
            MedicalTypeIds.SafetyCriticalWorker,
            MedicalTypeIds.GroupOneDriving,
            MedicalTypeIds.GroupTwoDriving,
            MedicalTypeIds.WorkAtHeight,
            MedicalTypeIds.WorkingConfinedSpaces,
            MedicalTypeIds.WearingRPE,
            MedicalTypeIds.WearingBreathingApparatus,
            MedicalTypeIds.WorkingThermalEnvironment,
            MedicalTypeIds.DSE,
            MedicalTypeIds.Offshore,
            MedicalTypeIds.Airside,
            MedicalTypeIds.OtherFitnessToWork,
            MedicalTypeIds.ManualHandlingMSKFaceToFaceAppointment,
            MedicalTypeIds.AmzPitPupMedical,
            MedicalTypeIds.LAAWorker,
            MedicalTypeIds.SkinFaceToFaceLevel1,
            MedicalTypeIds.SkinAssessmentLevel2
        ],
        questionConfigs: healthQuestions
    },
    DrugAlcoholTest: {
        id: "DrugAlcoholTest",
        title: "Drug and Alcohol Test",
        medicalTypes: [
            MedicalTypeIds.SkinFaceToFace,
            MedicalTypeIds.Audiometry,
            MedicalTypeIds.RespiratorySensitiser,
            MedicalTypeIds.RespiratorySensitiserFaceToFaceLevel1,
            MedicalTypeIds.RespiratorySensitiserQuestionnaireLevel1,
            MedicalTypeIds.HAVSFaceToFace,
            MedicalTypeIds.IonisingRadiationFaceToFace,
            MedicalTypeIds.BiologicalMonitoring,
            MedicalTypeIds.OtherSurveillance,
            MedicalTypeIds.SafetyCriticalWorker,
            MedicalTypeIds.GroupOneDriving,
            MedicalTypeIds.GroupTwoDriving,
            MedicalTypeIds.WorkAtHeight,
            MedicalTypeIds.WorkingConfinedSpaces,
            MedicalTypeIds.WearingRPE,
            MedicalTypeIds.WearingBreathingApparatus,
            MedicalTypeIds.WorkingThermalEnvironment,
            MedicalTypeIds.DSE,
            MedicalTypeIds.Offshore,
            MedicalTypeIds.Airside,
            MedicalTypeIds.OtherFitnessToWork,
            MedicalTypeIds.ManualHandlingMSKFaceToFaceAppointment,
            MedicalTypeIds.AmzPitPupMedical,
            MedicalTypeIds.LAAWorker,
            MedicalTypeIds.SkinFaceToFaceLevel1,
            MedicalTypeIds.SkinAssessmentLevel2
        ],
        questionConfigs: drugAlcoholTestQuestions
    },
    Urinalysis: {
        id: "Urinalysis",
        title: "Urinalysis",
        medicalTypes: [
            MedicalTypeIds.SafetyCriticalWorker,
            MedicalTypeIds.GroupOneDriving,
            MedicalTypeIds.GroupTwoDriving,
            MedicalTypeIds.WorkAtHeight,
            MedicalTypeIds.WorkingConfinedSpaces,
            MedicalTypeIds.WearingRPE,
            MedicalTypeIds.WearingBreathingApparatus,
            MedicalTypeIds.WorkingThermalEnvironment,
            MedicalTypeIds.Offshore,
            MedicalTypeIds.Airside,
            MedicalTypeIds.OtherFitnessToWork,
            MedicalTypeIds.AmzPitPupMedical
        ],
        questionConfigs: urinalysisQuestions
    },
    MsdAssessment: {
        id: "MsdAssessment",
        title: "MSD Assessment",
        medicalTypes: [
            MedicalTypeIds.SafetyCriticalWorker,
            MedicalTypeIds.GroupOneDriving,
            MedicalTypeIds.GroupTwoDriving,
            MedicalTypeIds.WorkAtHeight,
            MedicalTypeIds.WorkingConfinedSpaces,
            MedicalTypeIds.WearingRPE,
            MedicalTypeIds.WearingBreathingApparatus,
            MedicalTypeIds.WorkingThermalEnvironment,
            MedicalTypeIds.Offshore,
            MedicalTypeIds.Airside,
            MedicalTypeIds.OtherFitnessToWork,
            MedicalTypeIds.AmzPitPupMedical
        ],
        questionConfigs: msdQuestions
    },
    SkinAssessment: {
        id: "SkinAssessment",
        title: "Skin Assessment",
        medicalTypes: [
            MedicalTypeIds.SkinFaceToFace,
            MedicalTypeIds.SafetyCriticalWorker,
            MedicalTypeIds.WorkAtHeight,
            MedicalTypeIds.WorkingConfinedSpaces,
            MedicalTypeIds.WearingRPE,
            MedicalTypeIds.WearingBreathingApparatus,
            MedicalTypeIds.WorkingThermalEnvironment,
            MedicalTypeIds.Offshore,
            MedicalTypeIds.Airside,
            MedicalTypeIds.OtherFitnessToWork,
            MedicalTypeIds.LAAWorker,
            MedicalTypeIds.SkinFaceToFaceLevel1,
            MedicalTypeIds.SkinAssessmentLevel2
        ],
        questionConfigs: skinQuestions
    },
    Havs: {
        id: "Havs",
        title: "HAVS Assessment",
        medicalTypes: [MedicalTypeIds.HAVSFaceToFace],
        questionConfigs: havsQuestions
    },
    Examination: {
        id: "Examination",
        title: "Examination",
        label: "HAVS Examination",
        medicalTypes: [MedicalTypeIds.HAVSFaceToFace],
        questionConfigs: examinationQuestions

    },
    Audiometry: {
        id: "Audiometry",
        title: "Audiometry",
        medicalTypes: [
            MedicalTypeIds.Audiometry,
            MedicalTypeIds.SafetyCriticalWorker,
            MedicalTypeIds.Offshore,
            MedicalTypeIds.Airside,
            MedicalTypeIds.OtherFitnessToWork
        ],
        questionConfigs: audiometryQuestions
    },
    Spirometry: {
        id: "Spirometry",
        title: "Spirometry",
        medicalTypes: [
            MedicalTypeIds.RespiratorySensitiser,
            MedicalTypeIds.RespiratorySensitiserFaceToFaceLevel1,
            MedicalTypeIds.RespiratorySensitiserQuestionnaireLevel1,
            MedicalTypeIds.SafetyCriticalWorker,
            MedicalTypeIds.WorkingConfinedSpaces,
            MedicalTypeIds.WearingRPE,
            MedicalTypeIds.WearingBreathingApparatus,
            MedicalTypeIds.WorkingThermalEnvironment,
            MedicalTypeIds.Offshore,
            MedicalTypeIds.Airside,
            MedicalTypeIds.OtherFitnessToWork,
            MedicalTypeIds.LAAWorker
        ],
        questionConfigs: spirometryQuestions
    },
    Vision: {
        id: "Vision",
        title: "Vision",
        medicalTypes: [
            MedicalTypeIds.SkinFaceToFace,
            MedicalTypeIds.SafetyCriticalWorker,
            MedicalTypeIds.GroupOneDriving,
            MedicalTypeIds.GroupTwoDriving,
            MedicalTypeIds.WorkAtHeight,
            MedicalTypeIds.WorkingConfinedSpaces,
            MedicalTypeIds.WearingRPE,
            MedicalTypeIds.WearingBreathingApparatus,
            MedicalTypeIds.WorkingThermalEnvironment,
            MedicalTypeIds.DSE,
            MedicalTypeIds.Offshore,
            MedicalTypeIds.Airside,
            MedicalTypeIds.OtherFitnessToWork,
            MedicalTypeIds.AmzPitPupMedical,
            MedicalTypeIds.SkinFaceToFaceLevel1,
            MedicalTypeIds.SkinAssessmentLevel2
        ],
        questionConfigs: visionQuestions
    },
    BiologicalMonitoring: {
        id: "BiologicalMonitoring",
        title: "Biological Monitoring",
        medicalTypes: [MedicalTypeIds.BiologicalMonitoring],
        questionConfigs: biologicalMonitoringQuestions
    },
    ManualHandlingMSKQuestionnaire: {
        id: "ManualHandlingMSKQuestionnaire",
        title: "Manual Handling / MSK Questionnaire",
        label: "Manual Handling / MSK",
        medicalTypes: [MedicalTypeIds.ManualHandlingMSKFaceToFaceAppointment],
        questionConfigs: manualHandlingMSKQuestionnaireQuestions
    },
    GeneralClinicNotes: {
        id: "GeneralClinicNotes",
        title: "General Clinical Notes and Consent",
        medicalTypes: [
            MedicalTypeIds.SkinFaceToFace,
            MedicalTypeIds.Audiometry,
            MedicalTypeIds.RespiratorySensitiser,
            MedicalTypeIds.RespiratorySensitiserFaceToFaceLevel1,
            MedicalTypeIds.RespiratorySensitiserQuestionnaireLevel1,
            MedicalTypeIds.HAVSFaceToFace,
            MedicalTypeIds.IonisingRadiationFaceToFace,
            MedicalTypeIds.BiologicalMonitoring,
            MedicalTypeIds.OtherSurveillance,
            MedicalTypeIds.SafetyCriticalWorker,
            MedicalTypeIds.GroupOneDriving,
            MedicalTypeIds.GroupTwoDriving,
            MedicalTypeIds.WorkAtHeight,
            MedicalTypeIds.WorkingConfinedSpaces,
            MedicalTypeIds.WearingRPE,
            MedicalTypeIds.WearingBreathingApparatus,
            MedicalTypeIds.WorkingThermalEnvironment,
            MedicalTypeIds.DSE,
            MedicalTypeIds.Offshore,
            MedicalTypeIds.Airside,
            MedicalTypeIds.OtherFitnessToWork,
            MedicalTypeIds.ManualHandlingMSKFaceToFaceAppointment,
            MedicalTypeIds.AmzPitPupMedical,
            MedicalTypeIds.LAAWorker,
            MedicalTypeIds.SkinFaceToFaceLevel1,
            MedicalTypeIds.SkinAssessmentLevel2
        ],
        questionConfigs: generalAndConsentQuestions
    },
    BideltoidMeasurement: {
        id: "BideltoidMeasurement",
        title: "Bideltoid Measurement",
        medicalTypes: [],
        questionConfigs: bideltoidMeasurementQuestions
    },
    GripStrength: {
        id: "GripStrength",
        title: "Grip Strength",
        medicalTypes: [],
        questionConfigs: gripStrengthQuestions
    },
    ChesterStep: {
        id: "ChesterStep",
        title: "Chester Step",
        medicalTypes: [],
        questionConfigs: chesterStepQuestions
    },
    Other: {
        id: "Other",
        title: "Other",
        medicalTypes: [],
        questionConfigs: otherQuestions
    }
};
